// Iter 81 / Phase E0 — HR Document Templates admin.
// Listing + edit dialog. Uses a plain <textarea> for HTML body so
// operators have full control; live preview pane on the right.
import { useCallback, useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, FloppyDisk, X, Copy } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const CATEGORIES = [
    "offer_letter", "appointment_letter", "nda",
    "experience_letter", "relieving_letter", "promotion_letter",
    "warning_letter", "policy", "other",
];

const EMPTY = { code: "", title: "", category: "offer_letter", description: "", body_html: "", is_active: true };

export default function HrDocumentTemplates() {
    const { can } = useAuth();
    const [items, setItems] = useState([]);
    const [placeholders, setPlaceholders] = useState([]);
    const [editing, setEditing] = useState(null); // template doc or null
    const [draft, setDraft] = useState(EMPTY);
    const [open, setOpen] = useState(false);

    const reload = useCallback(() => {
        api.get("/hr/document-template/list")
            .then(({ data }) => {
                setItems(data.items || []);
                setPlaceholders(data.available_placeholders || []);
            })
            .catch((err) => toast.error(formatApiError(err, "Failed to load templates")));
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const openNew = () => {
        setEditing(null);
        setDraft(EMPTY);
        setOpen(true);
    };

    const openEdit = (tpl) => {
        setEditing(tpl);
        setDraft({ ...EMPTY, ...tpl });
        setOpen(true);
    };

    const save = async () => {
        if (!draft.code || !draft.title || !draft.body_html) {
            toast.error("Code, title and body are required.");
            return;
        }
        try {
            if (editing) {
                await api.patch(`/hr/document-template/${editing.code}`, {
                    title: draft.title,
                    category: draft.category,
                    description: draft.description,
                    body_html: draft.body_html,
                    is_active: draft.is_active,
                });
                toast.success("Template updated.");
            } else {
                await api.post("/hr/document-template", draft);
                toast.success("Template created.");
            }
            setOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };

    const remove = async (tpl) => {
        if (!window.confirm(`Delete template "${tpl.title}"?`)) return;
        try {
            const { data } = await api.delete(`/hr/document-template/${tpl.code}`);
            toast.success(data.note || `Template deleted.`);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="hr-templates-page">
            <PageHeader
                eyebrow="HR Configuration"
                title="Document Templates"
                description="HTML-based templates with {{employee.field}} placeholders. Generated documents inherit a permanent snapshot of the template version that was active at render time."
                actions={can("employee:admin") && (
                    <Button data-testid="hr-template-new-btn" onClick={openNew}><Plus size={14} className="mr-1" /> New Template</Button>
                )}
            />

            <div className="mt-6 grid grid-cols-1 gap-2">
                {items.length === 0 && (
                    <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500">
                        No templates yet. Click <em>New Template</em> to create one.
                    </div>
                )}
                {items.map((t) => (
                    <div key={t.code} data-testid={`hr-template-row-${t.code}`} className="bg-white rounded-xl border border-slate-200 p-5 flex justify-between items-start">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-sm font-semibold text-slate-800">{t.title}</h3>
                                <Badge variant="outline">{t.category}</Badge>
                                {t.is_active ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                                <Badge variant="outline">v{t.version}</Badge>
                            </div>
                            <div className="text-xs text-slate-500">Code: <span className="font-mono">{t.code}</span> · Updated {formatDate(t.updated_at)}</div>
                            {t.description && <div className="text-xs text-slate-600 mt-1">{t.description}</div>}
                        </div>
                        {can("employee:admin") && (
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`hr-template-edit-${t.code}`}><PencilSimple size={14} /></Button>
                                <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-rose-500"><Trash size={14} /></Button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="hr-template-dialog">
                    <DialogHeader><DialogTitle>{editing ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Code (unique)</Label>
                                    <Input data-testid="hr-template-code" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.replace(/[^a-zA-Z0-9_\-]/g, "") })} disabled={!!editing} placeholder="offer_letter_v2" />
                                </div>
                                <div>
                                    <Label className="text-xs">Category</Label>
                                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Title (also supports placeholders)</Label>
                                <Input data-testid="hr-template-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Offer Letter — {{employee.full_name}}" />
                            </div>
                            <div>
                                <Label className="text-xs">Description (internal)</Label>
                                <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                            </div>
                            <div>
                                <Label className="text-xs">HTML Body</Label>
                                <Textarea data-testid="hr-template-body" rows={18} className="font-mono text-xs" value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })} />
                            </div>
                            <label className="flex items-center gap-2 text-xs text-slate-600">
                                <input type="checkbox" checked={draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} /> Active
                            </label>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs">Available Placeholders (click to copy)</Label>
                                <div className="flex flex-wrap gap-1 mt-1 border border-slate-200 rounded-md p-2 bg-slate-50 max-h-48 overflow-y-auto" data-testid="hr-template-placeholders">
                                    {placeholders.map(([key, label]) => (
                                        <button key={key} onClick={() => { navigator.clipboard.writeText(`{{${key}}}`); toast.success(`Copied {{${key}}}`); }} className="bg-white text-[10px] font-mono px-2 py-1 rounded border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 text-slate-700">
                                            <Copy size={9} className="inline mr-1" />{key}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Live Preview (raw HTML, no data substitution)</Label>
                                <div data-testid="hr-template-preview" className="border border-slate-200 rounded-md p-3 bg-white text-xs max-h-[40vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: draft.body_html || "<p style='color:#94a3b8'>Empty body</p>" }} />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={save} data-testid="hr-template-save"><FloppyDisk size={14} className="mr-1" /> {editing ? "Save changes" : "Create template"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
